import React, { useState } from 'react';
import { api } from '../../App';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { Link, Copy, RefreshCw, Check, Clock, AlertCircle, ExternalLink, FileText, Loader2 } from 'lucide-react';
import { printPoliceForm } from '../../utils/policeFormTemplate';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const PRECHECKIN_STATUS = {
  NOT_STARTED: { label: 'Non commencé', variant: 'outline', icon: Clock, color: 'text-gray-500' },
  PARTIAL: { label: 'En cours', variant: 'warning', icon: AlertCircle, color: 'text-amber-600' },
  COMPLETED: { label: 'Complété', variant: 'success', icon: Check, color: 'text-green-600' }
};

export default function PrecheckinLink({ reservation, onUpdate }) {
  const [loading, setLoading] = useState(false);
  const [link, setLink] = useState(null);
  const [copied, setCopied] = useState(false);
  const [policeFormLoading, setPoliceFormLoading] = useState(false);

  const generateLink = async () => {
    setLoading(true);
    try {
      const response = await api.post(`/precheckin/reservations/${reservation.id}/token`);
      setLink(response.data.link);
      if (onUpdate) onUpdate();
      toast.success('Lien de pré check-in généré');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur');
    } finally {
      setLoading(false);
    }
  };

  const regenerateLink = async () => {
    if (!window.confirm('Regénérer le lien ? L\'ancien lien ne fonctionnera plus.')) return;
    
    setLoading(true);
    try {
      const response = await api.post(`/precheckin/reservations/${reservation.id}/token/regenerate`);
      setLink(response.data.link);
      if (onUpdate) onUpdate();
      toast.success('Nouveau lien généré');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur');
    } finally {
      setLoading(false);
    }
  };

  const copyLink = () => {
    const url = link || (reservation.precheckin_token ? 
      `${window.location.origin}/checkin/${reservation.precheckin_token}` : null);
    
    if (url) {
      navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success('Lien copié !');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const openLink = () => {
    const url = link || (reservation.precheckin_token ? 
      `${window.location.origin}/checkin/${reservation.precheckin_token}` : null);
    
    if (url) {
      window.open(url, '_blank');
    }
  };

  const viewPoliceForm = async () => {
    setPoliceFormLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/reservations/${reservation.id}/police-form`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      
      // Use centralized template
      printPoliceForm(data);
    } catch (err) {
      toast.error('Erreur lors du chargement de la fiche');
    } finally {
      setPoliceFormLoading(false);
    }
  };

  const status = PRECHECKIN_STATUS[reservation.precheckin_status] || PRECHECKIN_STATUS.NOT_STARTED;
  const StatusIcon = status.icon;
  const hasToken = reservation.precheckin_token || link;
  const isExpired = ['CHECKED_OUT', 'CANCELLED', 'NO_SHOW', 'FINISHED'].includes(reservation.status);
  const isCheckedIn = reservation.status === 'CHECKED_IN';

  // For finished reservations, show police form access if available
  if (isExpired) {
    const hasPoliceForm = reservation.police_form_data || reservation.police_form_generated_at;
    
    return (
      <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
        <p className="text-sm text-gray-500 flex items-center gap-2 mb-2">
          <Link className="w-4 h-4" />
          Pré check-in (réservation terminée)
        </p>
        {hasPoliceForm && (
          <Button 
            size="sm" 
            variant="outline" 
            onClick={viewPoliceForm}
            disabled={policeFormLoading}
          >
            {policeFormLoading ? (
              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
            ) : (
              <FileText className="w-4 h-4 mr-1" />
            )}
            Voir la fiche de police
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="p-4 bg-amber-50 rounded-lg border border-amber-200 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Link className="w-4 h-4 text-amber-600" />
          <span className="font-medium text-amber-900">Pré check-in externe</span>
        </div>
        <Badge variant={status.variant} className="flex items-center gap-1">
          <StatusIcon className="w-3 h-3" />
          {status.label}
        </Badge>
      </div>

      {/* Show police form button for checked-in reservations */}
      {isCheckedIn && (
        <div className="p-3 bg-green-50 rounded-lg border border-green-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-green-600" />
              <span className="text-sm text-green-800">Fiche de police générée</span>
            </div>
            <Button 
              size="sm" 
              variant="outline" 
              onClick={viewPoliceForm}
              disabled={policeFormLoading}
              className="border-green-300 text-green-700 hover:bg-green-100"
            >
              {policeFormLoading ? (
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              ) : (
                <FileText className="w-4 h-4 mr-1" />
              )}
              Consulter
            </Button>
          </div>
        </div>
      )}

      {!hasToken ? (
        <div>
          <p className="text-sm text-amber-700 mb-2">
            Générez un lien sécurisé pour permettre au client de renseigner les informations des passagers avant son arrivée.
          </p>
          <Button 
            size="sm" 
            onClick={generateLink} 
            disabled={loading}
            className="bg-amber-600 hover:bg-amber-700"
            data-testid="generate-precheckin-link"
          >
            {loading ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Link className="w-4 h-4 mr-2" />
            )}
            Générer le lien
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          <div className="flex items-center gap-2 bg-white p-2 rounded border border-amber-200 text-sm font-mono overflow-hidden">
            <span className="truncate flex-1 text-amber-800">
              {link || `${window.location.origin}/checkin/${reservation.precheckin_token}`}
            </span>
          </div>
          
          <div className="flex gap-2 flex-wrap">
            <Button size="sm" variant="outline" onClick={copyLink} data-testid="copy-precheckin-link">
              {copied ? (
                <Check className="w-4 h-4 mr-1 text-green-600" />
              ) : (
                <Copy className="w-4 h-4 mr-1" />
              )}
              {copied ? 'Copié !' : 'Copier'}
            </Button>
            <Button size="sm" variant="outline" onClick={openLink} data-testid="open-precheckin-link">
              <ExternalLink className="w-4 h-4 mr-1" />
              Ouvrir
            </Button>
            <Button 
              size="sm" 
              variant="ghost" 
              onClick={regenerateLink} 
              disabled={loading}
              className="text-amber-600"
            >
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Nouveau lien
            </Button>
          </div>

          {reservation.precheckin_status === 'COMPLETED' && reservation.precheckin_completed_at && (
            <p className="text-xs text-green-700">
              ✓ Complété le {new Date(reservation.precheckin_completed_at).toLocaleDateString('fr-FR')}
            </p>
          )}
          
          {reservation.precheckin_notes && (
            <div className="mt-2 p-2 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-xs font-medium text-blue-800 mb-1">Notes du client :</p>
              <p className="text-sm text-blue-700">{reservation.precheckin_notes}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
